import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AlertCircle, AlertTriangle, CheckCircle, InfoCircle, } from "@untitledui/icons";
import { PiInfo, PiX } from "react-icons/pi";
import { Toaster as Sonner, toast as sonnerToast } from "sonner";
import { tv } from "tailwind-variants";
import { match } from "ts-pattern";
import { cn } from "../cn";
import { Button } from "./button";
export const Toaster = () => {
    return (_jsx(Sonner, { className: "group ", position: "bottom-center", toastOptions: {
            classNames: {
                toast: "w-full",
            },
        } }));
};
export function toast({ kind, title, description, actions, autoHide, duration, }) {
    const resolvedDuration = duration ?? (autoHide ? 3000 : Number.POSITIVE_INFINITY);
    return sonnerToast.custom((id) => {
        return (_jsx("div", { className: "relative rounded-md shadow-popover", children: _jsx(Toast, { kind: kind, title: title, description: description, actions: actions?.({ dismiss: () => sonnerToast.dismiss(id) }), onClose: () => sonnerToast.dismiss(id) }) }));
    }, { duration: resolvedDuration });
}
function copyToClipboard(content) {
    return navigator.clipboard.writeText(content);
}
export const clickToCopy = async (e, copyText) => {
    e.preventDefault();
    e.stopPropagation();
    await copyToClipboard(copyText);
    toast({
        description: `${copyText} copied to clipboard`,
        kind: "info",
        autoHide: true,
    });
    return;
};
export const dismissToast = sonnerToast.dismiss;
// todo: [standardise-colours]
export const toastVariants = tv({
    slots: {
        // 1.5px border in the semantic tone (set per kind below) frames the toast;
        // a translucent 3px `ring` sits just outside the border as a soft tone-tinted
        // halo. `shadow-popover` is owned by the outer wrapper.
        root: "rounded-md border-[1.5px] ring-[3px]",
        title: "text-sm font-medium leading-normal",
        icon: "size-5",
    },
    variants: {
        kind: {
            default: {
                icon: "text-primary",
                title: "text-primary",
                // No explicit border colour — falls back to `borderColor.DEFAULT`
                // (neutral-400), which matches the muted feel of bg-secondary.
                root: "bg-secondary ring-[hsl(var(--color-neutral-500)/0.1)]",
            },
            info: {
                icon: "text-info",
                title: "text-info",
                root: "bg-info-muted border-info ring-[hsl(var(--color-info-600)/0.1)]",
            },
            warning: {
                icon: "text-warning",
                title: "text-warning",
                root: "bg-warning-muted border-warning ring-[hsl(var(--color-warning-600)/0.1)]",
            },
            error: {
                icon: "text-danger",
                title: "text-danger",
                root: "bg-danger-muted border-danger ring-[hsl(var(--color-danger-700)/0.1)]",
            },
            success: {
                icon: "text-success",
                title: "text-success",
                root: "bg-success-muted border-success ring-[hsl(var(--color-success-700)/0.1)]",
            },
        },
    },
    defaultVariants: { kind: "default" },
});
export function Toast({ title, description, actions, kind = "default", onClose, className, }) {
    const classes = toastVariants({ kind });
    const Icon = match(kind ?? "default")
        .with("info", () => PiInfo)
        .with("warning", () => AlertTriangle)
        .with("error", () => AlertCircle)
        .with("success", () => CheckCircle)
        .with("default", () => InfoCircle)
        .exhaustive();
    return (_jsxs("div", { className: cn(className, classes.root({
            className: "pointer-events-auto grid grid-flow-row grid-cols-[auto_1fr_auto] gap-x-3 gap-y-1 px-3 py-4",
        })), children: [_jsx("div", { className: "pt-px", children: _jsx(Icon, { className: classes.icon() }) }), title ? (_jsx("div", { className: classes.title({ className: "-mt-[1px] col-start-2" }), children: title })) : null, description ? (_jsx("div", { className: "col-start-2 text-primary text-sm leading-normal", children: description })) : null, actions ? _jsx("div", { className: "col-start-2 pt-2", children: actions }) : null, onClose ? (
            // Explicit grid placement + `self-start` pin the close button to
            // the top-right corner regardless of how many rows the body uses.
            // Negative margins extend the 40px hit target into the toast's
            // padding zone without visually inflating the chrome.
            _jsx(Button, { variant: "ghost", size: "md", icon: _jsx(PiX, {}), "aria-label": "Dismiss", onClick: onClose, className: "-mt-2 -mr-2 col-start-3 row-start-1 self-start text-secondary hover:text-primary" })) : null] }));
}
/**
 * Use this for a consistent layout when adding an actions row on the bottom of a toast.
 *
 * Example:
 *
 * ```tsx
 * <Toast
 *    title="Information"
 *    description="Something"
 *    actions={
 *      <ToastActions>
 *        <Button>Dismiss</Button>
 *      </ToastActions>
 *    }
 *  />
 *
 * ```
 */
export function ToastActions(props) {
    return _jsx("div", { className: "flex justify-start gap-3", children: props.children });
}
